(() => {
  const exactNormalise = (value) => String(value || '').trim().toLocaleLowerCase();
  const nameNormalise = (value) => exactNormalise(value).replace(/[^\p{L}\p{N}]+/gu, ' ').trim();
  const isRecord = (record) => record && record.productId != null && record.variantId != null
    && typeof record.productTitle === 'string' && typeof record.variantTitle === 'string'
    && typeof record.url === 'string' && Number.isInteger(Number(record.variantCount));

  const exactMatches = (records, query) => {
    const exactQuery = exactNormalise(query);
    if (!exactQuery) return [];
    const matched = new Map();
    records.forEach((record) => {
      if (exactNormalise(record.sku) === exactQuery || exactNormalise(record.barcode) === exactQuery) {
        matched.set(String(record.variantId), record);
      }
    });
    return [...matched.values()];
  };

  const searchRecords = (records, query) => {
    const exact = exactMatches(records, query);
    if (exact.length) return exact;
    const terms = nameNormalise(query).split(' ').filter(Boolean);
    if (!terms.length) return [];
    return records.filter((record) => {
      const name = nameNormalise(`${record.productTitle} ${record.variantTitle}`);
      const sku = exactNormalise(record.sku);
      const barcode = exactNormalise(record.barcode);
      return terms.every((term) => name.includes(term) || sku.includes(term) || barcode.includes(term));
    });
  };

  const validateIndex = (payload, expectedPage, expectedPages = null, expectedCatalogProductCount = null) => {
    const page = Number(payload?.page);
    const pages = Number(payload?.pages);
    const catalogProductCount = Number(payload?.catalogProductCount);
    const nextPage = payload?.nextPage == null ? null : Number(payload.nextPage);
    if (!payload || page !== expectedPage || !Number.isInteger(pages) || pages < expectedPage || !Number.isInteger(catalogProductCount) || catalogProductCount < 1 || !Array.isArray(payload.products) || !payload.products.length) {
      return {valid: false, reason: 'The product list could not be verified.'};
    }
    if ((expectedPages != null && pages !== expectedPages) || (expectedCatalogProductCount != null && catalogProductCount !== expectedCatalogProductCount)) return {valid: false, reason: 'The product list changed while it was loading.'};
    if ((page < pages && nextPage !== page + 1) || (page === pages && nextPage !== null)) return {valid: false, reason: 'The product list could not be verified.'};
    if (!payload.products.every(isRecord)) return {valid: false, reason: 'The product list could not be verified.'};
    return {valid: true};
  };

  const hasCompleteVariantSets = (records) => {
    const byProduct = new Map();
    records.forEach((record) => {
      const productId = String(record.productId);
      const current = byProduct.get(productId) || {declared: Number(record.variantCount), variants: new Set()};
      if (current.declared !== Number(record.variantCount)) current.inconsistent = true;
      current.variants.add(String(record.variantId));
      byProduct.set(productId, current);
    });
    return [...byProduct.values()].every((product) => !product.inconsistent && product.declared === product.variants.size);
  };
  const hasCompleteCatalog = (records, catalogProductCount) => new Set(records.map((record) => String(record.productId))).size === catalogProductCount;

  const resolver = {exactNormalise, nameNormalise, exactMatches, searchRecords, validateIndex, hasCompleteVariantSets, hasCompleteCatalog};
  if (typeof window === 'undefined') return;
  window.StudioProductFinderResolver = resolver;

  const maxResults = 12;

  function parseIndex(scope) {
    const script = scope.querySelector('[data-studio-product-finder-index]');
    if (!script) throw new Error('The product list could not be found.');
    return JSON.parse(script.textContent);
  }

  function appendResult(results, record) {
    const item = document.createElement('li');
    item.className = 'studio-product-finder__result';
    const link = document.createElement('a');
    link.href = record.url;

    const thumbnail = document.createElement('span');
    thumbnail.className = 'studio-product-finder__thumbnail';
    if (record.image) {
      const image = document.createElement('img');
      image.src = record.image;
      image.alt = '';
      image.width = 48;
      image.height = 48;
      image.loading = 'lazy';
      thumbnail.append(image);
    }

    const copy = document.createElement('span');
    copy.className = 'studio-product-finder__result-copy';
    const title = document.createElement('strong');
    title.textContent = record.productTitle;
    copy.append(title);
    link.append(thumbnail, copy);
    const details = [];
    if (record.variantTitle && record.variantTitle !== 'Default Title') details.push(record.variantTitle);
    if (record.sku) details.push(`SKU: ${record.sku}`);
    if (details.length) {
      const detail = document.createElement('small');
      detail.textContent = details.join(' · ');
      copy.append(detail);
    }
    item.append(link);
    const arrow = document.createElement('span');
    arrow.className = 'studio-product-finder__arrow';
    arrow.setAttribute('aria-hidden', 'true');
    arrow.textContent = '→';
    link.append(arrow);
    results.append(item);
    return link;
  }

  function initialiseFinder(root) {
    if (root.dataset.studioProductFinderInitialised === 'true') return;
    const form = root.querySelector('[data-studio-product-finder-form]');
    const input = root.querySelector('[data-studio-product-finder-input]');
    const status = root.querySelector('[data-studio-product-finder-status]');
    const results = root.querySelector('[data-studio-product-finder-results]');
    const retry = root.querySelector('[data-studio-product-finder-retry]');
    const retryButton = root.querySelector('[data-studio-product-finder-retry-button]');
    if (!form || !input || !status || !results || !retry || !retryButton) return;

    root.dataset.studioProductFinderInitialised = 'true';
    form.hidden = false;
    status.hidden = false;

    const state = {records: [], pages: 0, complete: false, loading: false, error: null, request: null, destroyed: false, lastQuery: input.value};
    let pageShowRestoreTimer = null;

    const setStatus = (message) => { status.textContent = message; };
    const clearResults = () => { results.replaceChildren(); };
    const setError = (message) => {
      state.error = message;
      state.complete = false;
      state.loading = false;
      clearResults();
      retry.hidden = false;
      setStatus(message);
    };
    const render = ({submitted = false} = {}) => {
      clearResults();
      if (state.error) return;
      if (!state.complete) {
        setStatus('Preparing product matches.');
        return;
      }
      const query = input.value;
      if (!query.trim()) {
        setStatus('');
        return;
      }
      const exact = exactMatches(state.records, query);
      const matches = searchRecords(state.records, query);
      if (!matches.length) {
        setStatus('No matching product was found. Try another name, size, SKU, or barcode.');
        return;
      }
      const list = document.createElement('ul');
      list.className = 'studio-product-finder__result-list';
      list.setAttribute('aria-label', 'Matching products');
      matches.slice(0, maxResults).forEach((record) => appendResult(list, record));
      results.append(list);
      if (submitted && exact.length > 1) setStatus('More than one exact match was found. Choose a product below.');
      else if (matches.length > maxResults) setStatus(`Showing the first ${maxResults} of ${matches.length} matches. Refine your search.`);
      else setStatus('Choose a matching product.');
      if (submitted) list.querySelector('a')?.focus();
    };

    const ingest = (payload, expectedPage, expectedPages = null, expectedCatalogProductCount = null) => {
      const validation = validateIndex(payload, expectedPage, expectedPages, expectedCatalogProductCount);
      if (!validation.valid) throw new Error(validation.reason);
      state.pages = Number(payload.pages);
      state.catalogProductCount = Number(payload.catalogProductCount);
      state.records.push(...payload.products);
      return payload;
    };
    const continuationURL = (page) => {
      const url = new URL(window.location.href);
      url.searchParams.set('section_id', root.dataset.sectionId);
      url.searchParams.set('page', String(page));
      url.hash = '';
      return url.toString();
    };
    const fetchIndexPage = async (page) => {
      const response = await fetch(continuationURL(page), {credentials: 'same-origin', signal: state.request.signal, headers: {Accept: 'text/html'}});
      if (!response.ok) throw new Error('The full product list could not be loaded.');
      const documentFragment = new DOMParser().parseFromString(await response.text(), 'text/html');
      const fragmentScript = documentFragment.querySelector('[data-studio-product-finder-index]');
      if (!fragmentScript) throw new Error('The full product list could not be loaded.');
      return JSON.parse(fragmentScript.textContent);
    };
    const load = async () => {
      state.error = null;
      state.complete = false;
      state.loading = true;
      state.records = [];
      retry.hidden = true;
      clearResults();
      setStatus('Preparing product matches.');
      state.request?.abort();
      state.request = new AbortController();
      try {
        const initialPage = Number(root.dataset.indexPage || 1);
        const firstPayload = initialPage === 1 ? parseIndex(root) : await fetchIndexPage(1);
        ingest(firstPayload, 1);
        for (let pageNumber = 2; pageNumber <= state.pages; pageNumber += 1) {
          const page = await fetchIndexPage(pageNumber);
          ingest(page, pageNumber, state.pages, state.catalogProductCount);
        }
        if (!hasCompleteCatalog(state.records, state.catalogProductCount) || !hasCompleteVariantSets(state.records)) throw new Error('The product list could not be verified.');
        if (state.destroyed) return;
        state.complete = true;
        state.loading = false;
        render();
      } catch (error) {
        if (error?.name === 'AbortError' || state.destroyed) return;
        setError(error?.message || 'The full product list could not be loaded.');
      }
    };

    const onInput = () => {
      state.lastQuery = input.value;
      render();
    };
    const onSubmit = (event) => {
      event.preventDefault();
      state.lastQuery = input.value;
      if (!state.complete || state.error) return;
      const matches = exactMatches(state.records, input.value);
      if (matches.length === 1) {
        window.location.assign(matches[0].url);
        return;
      }
      render({submitted: true});
    };
    const onPageShow = (event) => {
      if (!event.persisted || !state.lastQuery) return;
      window.clearTimeout(pageShowRestoreTimer);
      pageShowRestoreTimer = window.setTimeout(() => {
        pageShowRestoreTimer = null;
        if (state.destroyed || input.value === state.lastQuery) return;
        input.value = state.lastQuery;
        render();
      }, 0);
    };
    const onUnload = (event) => {
      if (event.target === root || event.target?.contains?.(root)) {
        state.destroyed = true;
        state.request?.abort();
        form.removeEventListener('submit', onSubmit);
        input.removeEventListener('input', onInput);
        retryButton.removeEventListener('click', load);
        document.removeEventListener('shopify:section:unload', onUnload);
        window.removeEventListener('pageshow', onPageShow);
        window.clearTimeout(pageShowRestoreTimer);
        delete root.dataset.studioProductFinderInitialised;
      }
    };

    form.addEventListener('submit', onSubmit);
    input.addEventListener('input', onInput);
    retryButton.addEventListener('click', load);
    document.addEventListener('shopify:section:unload', onUnload);
    window.addEventListener('pageshow', onPageShow);
    load();
  }

  const start = (scope = document) => {
    if (scope.matches?.('[data-studio-product-finder]')) initialiseFinder(scope);
    scope.querySelectorAll?.('[data-studio-product-finder]').forEach(initialiseFinder);
  };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => start(), {once: true});
  else start();
  document.addEventListener('shopify:section:load', (event) => start(event.target));
})();
